import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import { sortOptions } from '@components/show_page/sections/view_options/helper';
import { UserViewOptionsKeys } from '@components/show_page/sections/view_options/ViewOptionTypes';
import Toggle from '@components/common/toggle/main';
import { ListDropdown } from '@components/library/dropdown/index';
import { CloseButton, TextButton } from '@components/library/button/index';
import ViewOptionsLanguages from './view_options_languages';

import '@components/common/select_react/react_select_styles.scss';

const toggleSizes = {
  height: 26,
  width: 40,
  sliderDiameter: 22,
};

const ViewOptionsDropdown = ({
  isVisible,
  paginatePer,
  searchSubmitted,
  setRenderViewOptions,
  updateUserViewOptions,
  userViewOptions,
}) => {
  const viewOptionsRef = useRef(null);
  const {
    showAsGrid,
    assetsPerPage,
    selectedUGTLocale,
    showCustomFields,
    showEmptySections,
    sortOption,
    sortOptionSearch,
  } = userViewOptions;
  const { role } = BFG.currentUser;
  const collabable = role === 'owner' || role === 'admin' || role === 'collaborator';

  const assetsPerPageOptions = () => {
    const assetsPerPageValues = [10, 32, 75, 150, 300];
    const combinedValues = [paginatePer, ...assetsPerPageValues].sort((a, b) => (a - b));
    const assetsPerPageUpdate = [...new Set(combinedValues)]; // set ensures unique values

    return assetsPerPageUpdate.map((qty) => ({ value: qty, label: qty }));
  };

  // method copied from show-page.jsx
  const urlParams = () => {
    const params = {};
    window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => params[key] = value); // eslint-disable-line no-return-assign
    return params;
  };

  // using 'per=' as a url param take priority over user option
  const disableAssetsPerPage = Boolean(urlParams().per);

  return (
    <div
      className={`view-options ${isVisible ? 'visible' : ''}`}
      ref={viewOptionsRef}
    >
      <div className="view-options__item header">
        <p className="title-copy">
          {t('View Options')}
        </p>
        <CloseButton
          className="view-options__item--close"
          onClick={() => setRenderViewOptions(false)}
        />
      </div>
      <div className="view-options__item layout-item">
        <div className="view-options__item--row">
          <p className="view-options__item--row--copy">{t('Layout')}</p>
          <div className="layout-options">
            <button
              className={`toggle-grid-view layout-button ${showAsGrid ? 'active' : ''}`}
              onClick={showAsGrid ? undefined : () => updateUserViewOptions('showAsGrid', true)}
              type="button"
            >
              <span className="bff-grid-view icon" />
            </button>
            <button
              className={`toggle-list-view layout-button ${showAsGrid ? '' : 'active'}`}
              onClick={!showAsGrid ? undefined : () => updateUserViewOptions('showAsGrid', false)}
              type="button"
            >
              <span className="bff-list-view icon" />
            </button>
          </div>
        </div>
      </div>
      <div className="view-options__item">
        <div className={`view-options__item--row assets-per-page-row ${disableAssetsPerPage ? 'disabled' : ''}`}>
          <p className="view-options__item--row--copy">{t('Assets per section')}</p>
          <ListDropdown
            className="view-options__count-dropdown"
            onChange={(option) => updateUserViewOptions('assetsPerPage', option.value)}
            options={assetsPerPageOptions()}
            overflowParentRef={viewOptionsRef}
            value={assetsPerPage}
          />
        </div>
      </div>
      <div className="view-options__item sort-by-row">
        <div className="view-options__item--row">
          <p className="view-options__item--row--copy">{t('Sort by')}</p>
          <ListDropdown
            className="view-options__sort-dropdown--tablet"
            onChange={(option) => updateUserViewOptions(searchSubmitted ? UserViewOptionsKeys.SortOptionSearch : UserViewOptionsKeys.SortOption, option.value)}
            options={sortOptions(searchSubmitted)}
            overflowParentRef={viewOptionsRef}
            value={searchSubmitted ? sortOptionSearch : sortOption}
          />
        </div>
      </div>
      <div className="view-options__item">
        <div className="view-options__item--row">
          <p className="view-options__item--row--copy">{t('Show custom fields')}</p>
          <button
            className="show-custom-fields-button"
            onClick={(e) => {
              e.preventDefault();
              updateUserViewOptions('showCustomFields');
            }}
            type="button"
          >
            <Toggle
              sizes={toggleSizes}
              isOn={showCustomFields}
            />
          </button>
        </div>
        {collabable && (
          <div className="view-options__item--row show-empty-sections-row ">
            <p className="view-options__item--row--copy">{t('Show empty sections')}</p>
            <button
              className="show-empty-sections-button"
              onClick={(e) => {
                e.preventDefault();
                updateUserViewOptions('showEmptySections');
              }}
              type="button"
            >
              <Toggle
                sizes={toggleSizes}
                isOn={showEmptySections}
              />
            </button>
          </div>
        )}
        {BFG.context.hasFeature("translations") && (
          <div className="view-options__item--row language-menu">
            <p className="view-options__item--row--copy">{t('Asset info')}</p>
            <ViewOptionsLanguages
              isActiveLocaleInLocalStorage={userViewOptions.selectedUGTLocale !== undefined}
              activeLocale={selectedUGTLocale}
              updateUserViewOptions={updateUserViewOptions}
            />
          </div>
        )}
      </div>
      <div className="reset-row">
        <TextButton
          className="reset-button"
          onClick={() => updateUserViewOptions('reset', {})}
        >
          {t('Reset')}
        </TextButton>
      </div>
    </div>
  );
};

ViewOptionsDropdown.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  paginatePer: PropTypes.number.isRequired,
  searchSubmitted: PropTypes.bool,
  setRenderViewOptions: PropTypes.func.isRequired,
  updateUserViewOptions: PropTypes.func.isRequired,
  userViewOptions: PropTypes.shape({
    assetsPerPage: PropTypes.number,
    selectedUGTLocale: PropTypes.string,
    showAsGrid: PropTypes.bool,
    showCustomFields: PropTypes.bool,
    showEmptySections: PropTypes.bool,
    sortOption: PropTypes.string,
    sortOptionSearch: PropTypes.string,
  }),
};

ViewOptionsDropdown.defaultProps = {
  searchSubmitted: undefined,
  userViewOptions: {},
};

export default ViewOptionsDropdown;
